import React, { useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import styled from 'styled-components';

import AppContext from '../contexts/AppContext';
import ThemeContext from '../contexts/ThemeContext';
import FunctionsContext from '../contexts/FunctionsContext';

import PrebookingStepIndicator from '../components/PrebookingStepIndicator';
import PrebookingHeader from '../components/PrebookingHeader';
import usePrebookingState from '../hooks/usePrebookingState';
import PrebookingDetailsStep from './PrebookingDetailsStep';
import PrebookingPreferencesStep from './PrebookingPreferencesStep';
import PrebookingInvoicingStep from './PrebookingInvoicingStep';
import PrebookingSummaryStep from './PrebookingSummaryStep';
import ContractSelectionModal from '../components/ContractSelectionModal';
import {
  ButtonContainer,
  BackButton,
  NextButton,
  InterpreterCountBanner,
} from '../components/PrebookingSharedComponents';
import { ArrowBackIcon, PersonIcon } from '../components/PrebookingIcons';

import i18n from '../i18n';
import {
  createPrebooking,
  editPrebooking,
  cancelPrebooking,
  getPrebookings,
  getAttachmentUploadUrl,
  getAttachment,
  uploadFileToS3,
  confirmAttachmentUpload,
  prebookingPrequery,
} from '../services/prebookService';
import { getUsergroups } from '../services/usergroupService';
import { validateFormData, transformForAPI, transformFromAPI } from '../utils/prebookingTransformers';
import { showError } from '../services/toastService';
import { getActiveContractsFromUser, getContractById, getOrderableContractsFromUser, shouldBlockOrdering } from '../utils/contractUtils';

const StyledPrebookingWizard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  box-sizing: border-box;
`;

const WizardContainer = styled.div`
  width: 100%;
  box-sizing: border-box;
`;

const WizardContent = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  padding: 32px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 26px;
  font-weight: 600;
  color: ${({ colors }) => colors?.twilight || '#45446b'};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: ${({ colors }) => colors?.twilight || '#45446b'};
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.7;
  }
`;

const StepContent = styled.div`
  margin-top: 32px;
  min-height: 400px;
`;

const ViewModeButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  justify-content: flex-end;
`;

const EditButton = styled.button`
  padding: 12px 32px;
  border-radius: 25px;
  border: 2px solid ${({ colors }) => colors?.tulkaMainColor || '#5c56d4'};
  background: ${({ colors }) => colors?.tulkaMainColor || '#5c56d4'};
  color: white;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover { opacity: 0.9; }
`;

const CancelBookingButton = styled.button`
  padding: 12px 32px;
  border-radius: 25px;
  border: 2px solid ${({ colors }) => colors?.errorColor || '#ea7e8f'};
  background: transparent;
  color: ${({ colors }) => colors?.errorColor || '#ea7e8f'};
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover { opacity: 0.8; }
`;

const CopyButton = styled.button`
  padding: 12px 32px;
  border-radius: 25px;
  border: 2px solid ${({ colors }) => colors?.tulkaMainColor || '#5c56d4'};
  background: transparent;
  color: ${({ colors }) => colors?.tulkaMainColor || '#5c56d4'};
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover { opacity: 0.8; }
`;

const CancelOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.4);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CancelDialog = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  max-width: 400px;
  width: 90%;
  text-align: center;

  h3 {
    margin: 0 0 16px;
    font-family: 'Montserrat', sans-serif;
    color: #45446b;
  }

  p {
    margin: 0 0 24px;
    font-family: 'Montserrat', sans-serif;
    color: rgba(69, 68, 107, 0.8);
    font-size: 14px;
  }

  .dialog-buttons {
    display: flex;
    gap: 12px;
    justify-content: center;
  }
`;

const PrebookingWizard = () => {
  const { user } = useContext(AppContext);
  const { colors } = useContext(ThemeContext);
  const { setBusy, sessionExpired } = useContext(FunctionsContext);
  const history = useHistory();
  const { id: prebookingId } = useParams();
  const location = useLocation();

  // View/Edit mode state — check location.state for mode overrides from three dots menu
  const initialMode = prebookingId
    ? (location.state?.mode === 'edit' ? 'edit' : 'view')
    : 'create';
  const [wizardMode, setWizardMode] = useState(initialMode); // 'create' | 'view' | 'edit'
  const [showCancelConfirm, setShowCancelConfirm] = useState(location.state?.mode === 'cancel');
  const [showContractSelection, setShowContractSelection] = useState(false);

  const [currentStep, setCurrentStep] = useState(
    prebookingId ? (location.state?.mode === 'edit' ? 1 : 4) : 1
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isStepValid, setIsStepValid] = useState(true); // For step 2 validation
  const [attachmentFailures, setAttachmentFailures] = useState([]);
  const [createdPrebookings, setCreatedPrebookings] = useState([]);
  const [attachmentUploadProgress, setAttachmentUploadProgress] = useState(null);
  const [prequeryResult, setPrequeryResult] = useState(null);
  const [isPrequeryLoading, setIsPrequeryLoading] = useState(false);
  const [isLoadingPrebooking, setIsLoadingPrebooking] = useState(false);
  const [hasOriginalAttachment, setHasOriginalAttachment] = useState(false);
  // User groups prefetched in step 1 so the invoicing step (step 3) has them ready.
  const [userGroups, setUserGroups] = useState([]);
  const [userGroupsLoading, setUserGroupsLoading] = useState(true);
  const [isUserGroupSearchAllowed, setIsUserGroupSearchAllowed] = useState(false);
  const prequeryTimerRef = useRef(null);
  const prebookingState = usePrebookingState();
  const { formData, updateField, updateFields, applyContractTargetLanguages } = prebookingState;
  const selectedContractId = formData.selectedContractId;
  const selectedContractName = formData.selectedContractName;
  const activeContracts = useMemo(() => getActiveContractsFromUser(user), [user]);
  const orderableContracts = useMemo(() => getOrderableContractsFromUser(user), [user]);
  const orderingBlockedRef = useRef(false);

  // Refs to access step validation functions
  const step1Ref = useRef(null);
  const step3Ref = useRef(null);

  // Prefetch invoice user groups on mount so step 3 (invoicing) can render
  // immediately when the user reaches it, rather than waiting for a fetch then.
  useEffect(() => {
    let cancelled = false;
    const fetchUserGroups = async () => {
      setUserGroupsLoading(true);
      try {
        const response = await getUsergroups(null, true);
        if (cancelled) return;
        const groups = Array.isArray(response) ? response : response.groups || [];
        setUserGroups(groups);
        if (!Array.isArray(response) && response.options) {
          setIsUserGroupSearchAllowed(!!response.options.allowSearch);
        }
      } catch (e) {
        if (cancelled) return;
        console.error('Failed to fetch user groups:', e);
        if (e.message === 'Session expired') {
          sessionExpired();
        }
      } finally {
        if (!cancelled) setUserGroupsLoading(false);
      }
    };
    fetchUserGroups();
    return () => { cancelled = true; };
  }, [sessionExpired]);

  // Seed default competence levels as soon as contract + interpretation type are
  // known (step 1). Previously the levels were only populated when the user reached
  // step 2, causing the prequery to fire once without levels and then again after
  // entering step 2 — the UI showed the first (too-high) count until the user made
  // another change.
  const useInterpreterTier = useMemo(() => {
    const businessType = user?.userGroup?.businessType || user?.businessTypes?.[0];
    return businessType?.useInterpreterTier === true;
  }, [user]);

  useEffect(() => {
    if (!useInterpreterTier) return;
    if (formData.competenceLevel && formData.competenceLevel.length > 0) return;
    if (!formData.presence) return;

    const contract = getContractById(activeContracts, formData.selectedContractId)?.raw
      || activeContracts[0]?.raw
      || null;
    if (!contract?.allowedLevels?.length) return;

    let typeCode = 'prebooking_remote';
    if (formData.presence === 'onsite') typeCode = 'prebooking_onsite';
    else if (formData.presence === 'notice') typeCode = 'prebooking_notice';
    else if (formData.presence === 'remote' && (formData.mode === 'conference' || formData.mode === 'call')) typeCode = 'prebooking_external';

    const levelsForType = contract.allowedLevels.filter(l => l.interpretationTypeCode === typeCode);
    if (!levelsForType.length) return;

    const levelsRankOrdering = contract.levelsRankOrdering || 'minimum_best';
    const sortedIds = [...levelsForType]
      .sort((a, b) => levelsRankOrdering === 'maximum_best'
        ? a.interpreterTier - b.interpreterTier
        : b.interpreterTier - a.interpreterTier)
      .map(l => l.levelId);

    updateField('competenceLevel', sortedIds);
  }, [useInterpreterTier, formData.presence, formData.mode, formData.selectedContractId, formData.competenceLevel, activeContracts, updateField]);

  useEffect(() => {
    // Ensure user is authenticated
    if (!user || !user.id) {
      history.replace('/');
      return undefined;
    }

    setBusy(true);

    return () => {
      setBusy(false);
    };
  }, [user, history, setBusy]);

  // Initialize form from prebooking data when in view/edit mode
  useEffect(() => {
    if (!prebookingId) return;

    if (location.state?.prebooking) {
      // Data passed via navigation state
      const prebooking = location.state.prebooking;
      const prebookingContractId = prebooking?.contractId || prebooking?.contract?.id;
      const prebookingContract = getContractById(activeContracts, prebookingContractId)?.raw;
      const contractDistanceLimit = prebookingContract?.pbOnSiteKmLimit || prebooking?.contract?.pbOnSiteKmLimit || 0;
      const transformed = transformFromAPI(prebooking, { contractDistanceLimit });
      prebookingState.initializeFromPrebooking(transformed);
    } else {
      // Page was refreshed - fetch prebooking from API
      const fetchPrebooking = async () => {
        setIsLoadingPrebooking(true);
        try {
          const prebookings = await getPrebookings(true, user.type);
          const prebooking = prebookings?.find(p => p.id === prebookingId);
          if (prebooking) {
            const prebookingContractId = prebooking?.contractId || prebooking?.contract?.id;
            const prebookingContract = getContractById(activeContracts, prebookingContractId)?.raw;
            const contractDistanceLimit = prebookingContract?.pbOnSiteKmLimit || prebooking?.contract?.pbOnSiteKmLimit || 0;
            const transformed = transformFromAPI(prebooking, { contractDistanceLimit });
            prebookingState.initializeFromPrebooking(transformed);
          } else {
            console.error('Prebooking not found:', prebookingId);
            showError(i18n('prebooking_not_found') || 'Prebooking not found');
            history.replace('/');
          }
        } catch (error) {
          console.error('Failed to fetch prebooking:', error);
          showError(i18n('failed_to_load_prebooking') || 'Failed to load prebooking');
          history.replace('/');
        } finally {
          setIsLoadingPrebooking(false);
        }
      };
      fetchPrebooking();
    }
  }, [prebookingId, activeContracts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize form from cloned prebooking (copy from three dots menu or copy button)
  useEffect(() => {
    if (prebookingId) return;
    const clonePrebooking = location.state?.clonePrebooking;
    if (!clonePrebooking) return;

    const prebookingContractId = clonePrebooking?.contractId || clonePrebooking?.contract?.id;
    const prebookingContract = getContractById(activeContracts, prebookingContractId)?.raw;
    const contractDistanceLimit = prebookingContract?.pbOnSiteKmLimit || clonePrebooking?.contract?.pbOnSiteKmLimit || 0;
    const transformed = transformFromAPI(clonePrebooking, { contractDistanceLimit });
    prebookingState.initializeFromPrebooking(transformed);
  }, [activeContracts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if original prebooking has an attachment (for edit mode visibility)
  useEffect(() => {
    if (!prebookingId) return;
    getAttachment(prebookingId)
      .then(response => setHasOriginalAttachment(!!response))
      .catch(() => setHasOriginalAttachment(false));
  }, [prebookingId]);

  useEffect(() => {
    if (prebookingId) {
      return;
    }

    if (shouldBlockOrdering(user)) {
      setShowContractSelection(false);
      if (!orderingBlockedRef.current) {
        orderingBlockedRef.current = true;
        showError(i18n('no_orderable_contract', 'No contract is available for your languages. Please contact support.'), 'no-orderable-contract');
        history.replace(location.state?.returnTo || '/');
      }
      return;
    }

    const updateContractIfChanged = (contractId, contractName) => {
      if (selectedContractId === contractId && selectedContractName === contractName) {
        return false;
      }
      updateFields({
        selectedContractId: contractId,
        selectedContractName: contractName,
      });
      return true;
    };

    const routeContractId = location?.state?.selectedContractId;
    const routeContractName = location?.state?.selectedContractName;
    const routeContract = getContractById(activeContracts, routeContractId);

    if (routeContract) {
      updateContractIfChanged(routeContract.id, routeContract.name);
      setShowContractSelection(false);
      return;
    }

    if (routeContractId && routeContractName) {
      updateContractIfChanged(routeContractId, routeContractName);
      setShowContractSelection(false);
      return;
    }

    if (orderableContracts.length === 1) {
      updateContractIfChanged(orderableContracts[0].id, orderableContracts[0].name);
      setShowContractSelection(false);
      return;
    }

    if (orderableContracts.length > 1 && !selectedContractId) {
      setShowContractSelection(true);
    }
  }, [
    prebookingId,
    activeContracts,
    orderableContracts,
    user,
    history,
    location?.state?.returnTo,
    location?.state?.selectedContractId,
    location?.state?.selectedContractName,
    selectedContractId,
    selectedContractName,
    updateFields,
  ]);

  useEffect(() => {
    if (!selectedContractId || selectedContractName) {
      return;
    }

    const matchingContract = getContractById(activeContracts, selectedContractId);
    if (matchingContract) {
      updateField('selectedContractName', matchingContract.name);
    }
  }, [
    activeContracts,
    selectedContractId,
    selectedContractName,
    updateField,
  ]);

  // TUL-4996: narrow the target (customer) languages to the selected contract's allowed set.
  // Different contracts may permit different target languages, so the precise list is applied once a
  // contract is chosen (single-contract customers use their only contract). Falls back to the full
  // list when no contract restriction applies; the backend enforces the same rule at creation.
  useEffect(() => {
    const fullTargetLanguages = user?.targetLanguages || [];
    const selectedContract = getContractById(activeContracts, formData.selectedContractId)
      || (activeContracts.length === 1 ? activeContracts[0] : null);

    applyContractTargetLanguages(fullTargetLanguages, selectedContract?.targetLanguageIds);
  }, [formData.selectedContractId, activeContracts, user, applyContractTargetLanguages]);

  const handleContractSelected = (contract) => {
    updateFields({
      selectedContractId: contract.id,
      selectedContractName: contract.name,
    });
    setShowContractSelection(false);
  };

  // Debounced prequery call when language + time slot are available
  const runPrequery = useCallback(() => {
    if (prequeryTimerRef.current) clearTimeout(prequeryTimerRef.current);
    prequeryTimerRef.current = setTimeout(async () => {
      const hasLanguage = !!formData.selectedLanguageId;
      const isAnnouncement = formData.presence === 'notice';
      const hasTimeSlot = formData.timeSlots?.some(s => s.date && s.startTime && s.duration);
      const hasDeliveryDate = isAnnouncement && !!formData.lastDeliveryDate;

      if (!hasLanguage || (!hasTimeSlot && !hasDeliveryDate)) {
        setPrequeryResult(null);
        setIsPrequeryLoading(false);
        return;
      }

      setIsPrequeryLoading(true);

      let combinedDate;
      let durationMinutes;

      if (isAnnouncement) {
        // For announcements, use lastDeliveryDate at 18:00 (same as submission logic)
        combinedDate = new Date(formData.lastDeliveryDate);
        combinedDate.setHours(18, 0, 0, 0);
        const contentLength = formData.messageContent?.length || 0;
        durationMinutes = contentLength <= 500 ? 15 : 30;
      } else {
        const slot = formData.timeSlots.find(s => s.date && s.startTime && s.duration);
        combinedDate = new Date(slot.date);
        combinedDate.setHours(slot.startTime.getHours(), slot.startTime.getMinutes(), 0, 0);
        durationMinutes = slot.duration;
      }

      try {
        // The interpretation mode (call/video/conference) only applies to
        // remote presence. Scope mode handling to remote so a stale mode left
        // over from the remote view (e.g. Conference then switched to On-site)
        // doesn't query for the wrong presence/callType.
        let presence = formData.presence || 'remote';
        let callType;
        if (presence === 'remote') {
          if (formData.mode === 'conference') {
            callType = 'data';
            presence = 'external';
          } else if (formData.mode === 'call') {
            callType = 'call';
            presence = 'external';
          } else {
            callType = 'data';
          }
        } else if (presence === 'notice') {
          callType = 'call';
        } else {
          // onsite: callType is not user-selectable; default to 'call'.
          callType = 'call';
        }

        // Validate final values
        const validCallTypes = ['call', 'data'];
        const validPresences = ['remote', 'onsite', 'external', 'notice'];

        if (!validCallTypes.includes(callType)) {
          console.error(`Invalid callType: ${callType}. Expected one of: ${validCallTypes.join(', ')}. Defaulting to 'data'.`);
          callType = 'data';
        }

        if (!validPresences.includes(presence)) {
          console.error(`Invalid presence: ${presence}. Expected one of: ${validPresences.join(', ')}. Defaulting to 'remote'.`);
          presence = 'remote';
        }

        const skillIds = [
          formData.selectedDialect,
          formData.gender !== 'does_not_matter' ? formData.gender : null,
          formData.experienceSkill !== 'does_not_matter_experience' ? formData.experienceSkill : null,
        ].filter(Boolean);

        const allowedLevelJoins = (formData.competenceLevel || [])
          .filter(id => id && id !== 'no-choice')
          .map((levelId, index) => ({ levelId, sortOrder: index + 1 }));

        // Fold the fallback-language consent into a single id, mirroring the
        // submission transformer (prebookingTransformers.js). The backend
        // prequery shares getPrebookingInterpreters, which queries the
        // alternative language and merges/dedupes it into the result, so the
        // displayed count includes the fallback pool too (TUL-4519).
        let alternativeLanguageId = null;
        if (formData.acceptAlternativeLanguage === true && formData.alternativeLanguageId) {
          alternativeLanguageId = formData.alternativeLanguageId;
        }

        const result = await prebookingPrequery({
          customerId: user.customerId,
          interpretedLanguageId: formData.selectedLanguageId,
          targetLanguageId: formData.targetLanguages?.find(l => l.id === formData.selectedTargetLanguageId)?.languageId || null,
          interpretationDate: combinedDate.getTime(),
          durationEstimated: durationMinutes * 60,
          presence,
          callType,
          businessTypeId: user.userGroup?.businessType?.id || user.businessTypes?.[0]?.id || null,
          contractId: selectedContractId,
          skillIds,
          ...(allowedLevelJoins.length > 0 ? { allowedLevelJoins } : {}),
          ...(alternativeLanguageId ? { acceptAlternativeLanguage: true, alternativeLanguageId } : {}),
        });
        setPrequeryResult(result);
      } catch {
        setPrequeryResult(null);
      } finally {
        setIsPrequeryLoading(false);
      }
    }, 500);
  }, [formData.selectedLanguageId, formData.selectedTargetLanguageId, formData.timeSlots, formData.presence, formData.mode, formData.selectedDialect, formData.targetLanguages, formData.gender, formData.experienceSkill, formData.competenceLevel, formData.lastDeliveryDate, formData.messageContent, formData.acceptAlternativeLanguage, formData.alternativeLanguageId, selectedContractId, user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    runPrequery();
    return () => {
      if (prequeryTimerRef.current) clearTimeout(prequeryTimerRef.current);
    };
  }, [runPrequery, formData.presence]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    history.replace(location.state?.returnTo || '/');
  };

  const handleEditPrebooking = () => {
    setWizardMode('edit');
    setCurrentStep(1);
  };

  const handleCopyPrebooking = () => {
    const prebooking = location.state?.prebooking;
    if (prebooking) {
      history.push('/book-interpretation', { clonePrebooking: prebooking });
    }
  };

  const handleCancelPrebooking = async () => {
    try {
      setIsSubmitting(true);
      setBusy(true);
      await cancelPrebooking(prebookingId, { reason: '', userType: 'Customer' });
      history.replace(location.state?.returnTo || '/');
    } catch (error) {
      console.error('Failed to cancel prebooking:', error);
      showError(error.message || i18n('failed_to_cancel_prebooking'));
    } finally {
      setIsSubmitting(false);
      setBusy(false);
      setShowCancelConfirm(false);
    }
  };

  const handleNext = () => {
    // Validate current step before proceeding
    if (currentStep === 1 && step1Ref.current?.validate) {
      if (!step1Ref.current.validate()) {
        return; // Validation failed, don't proceed
      }
    }
    if (currentStep === 3 && step3Ref.current?.validate) {
      if (!step3Ref.current.validate()) {
        return; // Validation failed, don't proceed
      }
    }
    // Step 2 validation handled via isStepValid state

    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Helper function to upload attachment to a prebooking (TUL-3848)
  const uploadAttachmentToPrebooking = async (targetPrebookingId, attachment, replace = false, onProgress) => {
    try {
      // Step 1: Get presigned URL
      const uploadUrlResponse = await getAttachmentUploadUrl(
        targetPrebookingId,
        attachment.filename,
        attachment.size,
        attachment.contentType,
        null,
        null,
        replace
      );

      if (!uploadUrlResponse?.uploadUrl) {
        throw new Error('Failed to get upload URL');
      }

      // Step 2: Upload file to S3
      const uploadSuccess = await uploadFileToS3(
        uploadUrlResponse.uploadUrl,
        attachment.file,
        { onProgress }
      );

      if (!uploadSuccess) {
        throw new Error('Failed to upload file to S3');
      }

      // Step 3: Confirm upload
      const confirmResponse = await confirmAttachmentUpload(targetPrebookingId, uploadUrlResponse.attachmentId);

      if (!confirmResponse?.success) {
        throw new Error('Failed to confirm upload');
      }

      return true;
    } catch (error) {
      // If attachment already exists (409), retry with replace=true (only once)
      const status = error.status || error.response?.status;
      const isAlreadyExists = status === 409 ||
        (error.message && error.message.toLowerCase().includes('already exists'));

      // Only retry if we haven't already tried with replace=true to prevent infinite recursion
      if (isAlreadyExists && !replace) {
        console.log('Attachment already exists, retrying with replace=true');
        return uploadAttachmentToPrebooking(targetPrebookingId, attachment, true, onProgress);
      }

      // If we already tried with replace=true and still got 409, or any other error, don't retry
      console.error('Failed to upload attachment:', error);
      throw error;
    }
  };

  const uploadAttachmentsForPrebookings = async (prebookings) => {
    if (!prebookingState.formData.attachment || !prebookings?.length) {
      return [];
    }

    const failures = [];

    for (const booking of prebookings) {
      const bookingPrebookingId = booking.prebookingId;
      if (!bookingPrebookingId) {
        continue;
      }
      try {
        setAttachmentUploadProgress(0);
        await uploadAttachmentToPrebooking(
          bookingPrebookingId,
          prebookingState.formData.attachment,
          false,
          (loaded, total) => {
            if (!total) return;
            const progress = Math.round((loaded / total) * 100);
            setAttachmentUploadProgress(progress);
          }
        );
      } catch (attachmentError) {
        console.error(`Failed to upload attachment for prebooking ${bookingPrebookingId}:`, attachmentError);
        failures.push({
          prebookingId: bookingPrebookingId,
          error: attachmentError.message || i18n('attachment_upload_failed'),
        });
      } finally {
        setAttachmentUploadProgress(null);
      }
    }

    return failures;
  };

  const retryAttachmentUploads = async () => {
    if (!createdPrebookings.length || !attachmentFailures.length) {
      return;
    }
    try {
      setIsSubmitting(true);
      setBusy(true);

      const failedIds = new Set(attachmentFailures.map((failure) => failure.prebookingId));
      const toRetry = createdPrebookings.filter((item) => failedIds.has(item.prebookingId));
      const retryResults = await uploadAttachmentsForPrebookings(toRetry);

      setAttachmentFailures(retryResults);

      if (retryResults.length === 0) {
        prebookingState.resetForm();
        setAttachmentUploadProgress(null);
        setAttachmentFailures([]);
        setCreatedPrebookings([]);
        history.replace('/');
      } else {
        showError(i18n('attachment_upload_partial_failed') || `${retryResults.length} attachment(s) failed to upload.`);
      }
    } catch (error) {
      console.error('Failed to retry attachment uploads:', error);
      showError(error.message || i18n('attachment_upload_failed'));
    } finally {
      setIsSubmitting(false);
      setBusy(false);
    }
  };

  const handleSubmit = async () => {
    if (createdPrebookings.length > 0 && attachmentFailures.length > 0) {
      await retryAttachmentUploads();
      return;
    }

    // Validate form data
    const errors = validateFormData(prebookingState.formData);
    if (errors.length > 0) {
      showError(errors[0]); // Show first error
      return;
    }

    setAttachmentFailures([]);
    setCreatedPrebookings([]);

    setIsSubmitting(true);
    setBusy(true);

    const { timeSlots, attachment, ...formDataWithoutSlots } = prebookingState.formData;

    // Edit mode: call editPrebooking instead of create
    if (wizardMode === 'edit' && prebookingId) {
      setIsSubmitting(true);
      setBusy(true);
      try {
        const slot = timeSlots[0] || {};
        const prebookData = transformForAPI(formDataWithoutSlots, slot);
        const originalData = prebookingState.originalEditData?.current;
        let originalPrebookData = null;
        if (originalData) {
          // eslint-disable-next-line no-unused-vars
          const { timeSlots: origSlots, attachment: _origAttachment, ...origRest } = originalData;
          originalPrebookData = transformForAPI(origRest, origSlots[0] || {});
        }
        await editPrebooking(prebookData, prebookingId, originalPrebookData);
        if (attachment) {
          await uploadAttachmentToPrebooking(prebookingId, attachment);
        }
        prebookingState.resetForm();
        history.replace('/');
      } catch (error) {
        console.error('Failed to edit prebooking:', error);
        showError(error.message || i18n('failed_to_edit_prebooking'));
      } finally {
        setIsSubmitting(false);
        setBusy(false);
      }
      return;
    }

    const results = { succeeded: [], failed: [] };

    try {
      // For announcements, create a synthetic time slot from lastDeliveryDate
      let slotsToProcess = timeSlots;

      if (formDataWithoutSlots.presence === 'notice') {
        // For announcements, use lastDeliveryDate at 18:00
        const deliveryDate = new Date(formDataWithoutSlots.lastDeliveryDate);
        deliveryDate.setHours(18, 0, 0, 0);

        // Calculate duration based on message content length
        const contentLength = formDataWithoutSlots.messageContent?.length || 0;
        const duration = contentLength <= 500 ? 15 : 30;

        slotsToProcess = [{
          date: deliveryDate,
          startTime: deliveryDate,
          duration: duration
        }];
      }

      // Create prebookings sequentially
      for (let i = 0; i < slotsToProcess.length; i++) {
        const slot = slotsToProcess[i];

        try {
          // Transform form data to prebooking format using shared transformer
          const prebookData = transformForAPI(formDataWithoutSlots, slot);

          const result = await createPrebooking(prebookData);

          if (result && !result.error) {
            // Handle different response formats: array ['id'], object {_id}, or object {id}
            const createdId = Array.isArray(result) ? result[0] : (result._id || result.id);
            results.succeeded.push({ slot, result, prebookingId: createdId });
          } else {
            throw new Error(result.error || 'Failed to create prebooking');
          }
        } catch (slotError) {
          console.error(`Failed to create prebooking slot ${i + 1}:`, slotError);
          results.failed.push({
            slot,
            slotIndex: i + 1,
            error: slotError.message || i18n('failed_to_create_prebooking')
          });
          // Continue processing remaining slots
        }
      }

      let attachmentFailed = [];
      if (attachment && results.succeeded.length > 0) {
        attachmentFailed = await uploadAttachmentsForPrebookings(results.succeeded);
      }

      // Handle final results
      if (results.failed.length === 0) {
        // All prebookings succeeded
        if (attachmentFailed.length > 0) {
          // Prebookings succeeded but some attachments failed
          setCreatedPrebookings(results.succeeded);
          setAttachmentFailures(attachmentFailed);
          setCurrentStep(4);
          showError(i18n('attachment_upload_partial_failed') || `${attachmentFailed.length} attachment(s) failed to upload.`);
          return;
        }
        // Navigate away
        prebookingState.resetForm();
        setAttachmentFailures([]);
        setAttachmentUploadProgress(null);
        setCreatedPrebookings([]);
        history.replace('/');
      } else if (results.succeeded.length === 0) {
        // All failed
        throw new Error(i18n('failed_to_create_all_prebookings'));
      } else {
        // Partial success - show detailed error
        let message = `${results.succeeded.length} ${i18n('succeeded')}, ${results.failed.length} ${i18n('failed')}`;

        const failedDetails = results.failed.map(f =>
          `${i18n('slot')} ${f.slotIndex}: ${f.error}`
        ).join(', ');

        if (failedDetails) {
          message += `: ${failedDetails}`;
        }

        // Include attachment failures if any
        if (attachmentFailed.length > 0) {
          message += `. ${i18n('attachment_upload_partial_failed') || `${attachmentFailed.length} attachment(s) failed to upload.`}`;
          setAttachmentFailures(attachmentFailed);
          setCreatedPrebookings(results.succeeded);
        }

        showError(message);
      }
    } catch (error) {
      console.error('Failed to create prebooking:', error);
      showError(error.message || i18n('failed_to_create_prebooking'));
    } finally {
      setIsSubmitting(false);
      setBusy(false);
    }
  };

  const renderWizardButtons = () => {
    // View mode: show Edit and Cancel buttons only if prebooking is not cancelled
    if (wizardMode === 'view' && currentStep === 4) {
      const isCancelled = location.state?.prebooking?.cancelledAt;
      if (isCancelled) {
        return null; // Don't show any action buttons for cancelled prebookings
      }
      return (
        <ViewModeButtons>
          <CancelBookingButton colors={colors} onClick={() => setShowCancelConfirm(true)} disabled={isSubmitting}>
            {i18n('cancel_prebook')}
          </CancelBookingButton>
          <CopyButton colors={colors} onClick={handleCopyPrebooking}>
            {i18n('copy_reservation_description')}
          </CopyButton>
          <EditButton colors={colors} onClick={handleEditPrebooking}>
            {i18n('update_prebook')}
          </EditButton>
        </ViewModeButtons>
      );
    }

    const interpreterCount = prequeryResult?.interpreters?.length;

    // Step 1: Only Next button, no banner
    if (currentStep === 1) {
      return (
        <ButtonContainer>
          <NextButton colors={colors} onClick={handleNext}>
            {i18n('next')}
          </NextButton>
        </ButtonContainer>
      );
    }

    // Steps 2-4: Back + Banner + Next
    const showBanner = currentStep >= 2 && (isPrequeryLoading || interpreterCount !== null);
    const isLastStep = currentStep === 4;
    const isEditMode = wizardMode === 'edit';

    return (
      <ButtonContainer hasBackButton>
        <BackButton onClick={handleBack}>
          <ArrowBackIcon color="#45446b" size={20} />
          {isLastStep ? i18n('back_to_edit') : i18n('back')}
        </BackButton>

        {showBanner && (
          <InterpreterCountBanner colors={colors}>
            <div className="user-icons">
              <PersonIcon color="#ffffff" size={20} />
              <PersonIcon color="#ffffff" size={20} />
            </div>
            {isPrequeryLoading ? (
              <span>{i18n('searching_interpreters', 'Searching interpreters…')}</span>
            ) : (
              <>
                <span className="interpreter-count">{interpreterCount}</span> {i18n('interpreters_found_for_your_selections')}
              </>
            )}
          </InterpreterCountBanner>
        )}

        <NextButton
          colors={colors}
          onClick={isLastStep ? handleSubmit : handleNext}
          disabled={currentStep === 2 ? !isStepValid : isLastStep ? (isSubmitting || attachmentUploadProgress !== null) : false}
        >
          {isLastStep
            ? (isSubmitting ? i18n('submitting') : (isEditMode ? i18n('update') : i18n('confirm_order')))
            : i18n('next')}
        </NextButton>
      </ButtonContainer>
    );
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <PrebookingDetailsStep
            ref={step1Ref}
            formData={prebookingState.formData}
            updateField={prebookingState.updateField}
            addTimeSlot={prebookingState.addTimeSlot}
            removeTimeSlot={prebookingState.removeTimeSlot}
            updateTimeSlot={prebookingState.updateTimeSlot}
            wizardMode={wizardMode}
          />
        );
      case 2:
        return (
          <PrebookingPreferencesStep
            formData={prebookingState.formData}
            updateFields={prebookingState.updateFields}
            onValidation={setIsStepValid}
          />
        );
      case 3:
        return (
          <PrebookingInvoicingStep
            ref={step3Ref}
            formData={prebookingState.formData}
            updateField={prebookingState.updateField}
            updateAttachment={prebookingState.updateAttachment}
            clearAttachment={prebookingState.clearAttachment}
            addEmail={prebookingState.addEmail}
            removeEmail={prebookingState.removeEmail}
            addPhone={prebookingState.addPhone}
            removePhone={prebookingState.removePhone}
            prebookingId={prebookingId}
            wizardMode={wizardMode}
            hasOriginalAttachment={hasOriginalAttachment}
            preloadedUserGroups={userGroups}
            preloadedUserGroupsLoading={userGroupsLoading}
            preloadedIsUserGroupSearchAllowed={isUserGroupSearchAllowed}
          />
        );
      case 4:
        return (
          <PrebookingSummaryStep
            formData={prebookingState.formData}
            prebookingId={prebookingId}
            wizardMode={wizardMode}
            originalInterpretationDate={location.state?.prebooking?.interpretationDate}
          />
        );
      default:
        return null;
    }
  };

  return (
    <StyledPrebookingWizard colors={colors}>
      <WizardContainer colors={colors} data-test="prebooking-wizard-container">
        <PrebookingHeader onCancel={handleClose} />

        <WizardContent>
          <Header>
            <Title colors={colors}>
              {wizardMode === 'view'
                ? i18n('prebooking_details')
                : wizardMode === 'edit'
                  ? i18n('update_prebook')
                  : i18n('book_interpretation')}
            </Title>
            <CloseButton colors={colors} onClick={handleClose} title={i18n('back')}>
              ×
            </CloseButton>
          </Header>

          {wizardMode !== 'view' && (
            <PrebookingStepIndicator currentStep={currentStep} />
          )}

          <StepContent>
            {isLoadingPrebooking ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(69, 68, 107, 0.6)', fontFamily: 'Montserrat, sans-serif' }}>
                {i18n('loading') || 'Loading...'}
              </div>
            ) : (
              renderStep()
            )}
          </StepContent>

          {attachmentFailures.length > 0 && (
            <div style={{ marginTop: '12px', color: colors.errorColor || '#ea7e8f' }}>
              {i18n('attachment_upload_partial_failed') || 'Attachment upload failed for some bookings. Click confirm to retry.'}
            </div>
          )}
          {attachmentUploadProgress !== null && (
            <div style={{ marginTop: '8px', color: colors.textSecondary || 'rgba(69, 68, 107, 0.8)' }}>
              {`Attachment upload ${attachmentUploadProgress}%`}
            </div>
          )}

          {renderWizardButtons()}
        </WizardContent>

        {showCancelConfirm && (
          <CancelOverlay onClick={() => setShowCancelConfirm(false)}>
            <CancelDialog onClick={(e) => e.stopPropagation()}>
              <h3>{i18n('cancel_prebook')}</h3>
              <p>{i18n('cancel_prebook_confirmation_message')}</p>
              <div className="dialog-buttons">
                <CancelBookingButton colors={colors} onClick={() => setShowCancelConfirm(false)}>
                  {i18n('no')}
                </CancelBookingButton>
                <EditButton colors={colors} onClick={handleCancelPrebooking} disabled={isSubmitting}>
                  {isSubmitting ? i18n('submitting') : i18n('yes')}
                </EditButton>
              </div>
            </CancelDialog>
          </CancelOverlay>
        )}

        <ContractSelectionModal
          isOpen={showContractSelection}
          contracts={orderableContracts}
          colors={colors}
          onSelect={handleContractSelected}
          onClose={handleClose}
        />
      </WizardContainer>
    </StyledPrebookingWizard>
  );
};

export default PrebookingWizard;
